import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useAdminAuth } from './AdminAuthContext';
import { updateMyTheme, updateProviderTheme } from '../services/themeApi';
import { updateAdminTheme } from '../services/adminApi';

const ThemeContext = createContext(null);

const DASHBOARD_DEFAULT = { mode: 'light', accent: 'blue' };
const ADMIN_DEFAULT     = { mode: 'dark',  accent: 'blue' };

export const ThemeProvider = ({ children }) => {
  const { user, patchUser } = useAuth();
  const { admin, patchAdmin } = useAdminAuth();

  const dashboardTheme = user?.themePreference ?? DASHBOARD_DEFAULT;
  const adminTheme = admin?.themePreference ?? ADMIN_DEFAULT;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dashboardTheme.mode);
    document.documentElement.setAttribute('data-accent', dashboardTheme.accent);
  }, [dashboardTheme.mode, dashboardTheme.accent]);

  useEffect(() => {
    document.documentElement.setAttribute('data-admin-theme', adminTheme.mode);
    document.documentElement.setAttribute('data-admin-accent', adminTheme.accent);
  }, [adminTheme.mode, adminTheme.accent]);

  const setDashboardTheme = useCallback(async (partial) => {
    if (!user) return;
    const res = user.role === 'user'
      ? await updateMyTheme(partial.mode, partial.accent)
      : await updateProviderTheme(partial.mode, partial.accent);
    patchUser({ themePreference: res.data.themePreference });
  }, [user, patchUser]);

  const setAdminTheme = useCallback(async (partial) => {
    if (!admin) return;
    const res = await updateAdminTheme(partial.mode, partial.accent);
    patchAdmin({ themePreference: res.data.themePreference });
  }, [admin, patchAdmin]);

  const value = useMemo(() => ({
    dashboardTheme,
    adminTheme,
    setDashboardTheme,
    setAdminTheme
  }), [dashboardTheme, adminTheme, setDashboardTheme, setAdminTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};
